import { api } from "@/lib/api";
import { oznaciAktivnost } from "@/lib/sesija";
import type { ObvezaTvrtke } from "@/lib/companyObligations";
import type { InboxBatchResponse, InboxDetail, InboxItem, InboxOwner, InboxPage, InboxSite, InboxUploadSource, InboxUploadResult } from "@/lib/documentInbox";

export interface CompanyInboxTarget {
  company_obligation_id?: string | null;
  site_obligation_id?: string | null;
  occurrence_id?: string | null;
}
export interface CompanyInboxConfirmBody extends CompanyInboxTarget {
  naziv: string;
  opis: string | null;
  kategorija: string;
  complete_obligation: boolean;
  completed_on: string | null;
  completion_note: string | null;
}
export function companyInboxLinked(item: CompanyInboxTarget): boolean {
  return Boolean(item.company_obligation_id || item.site_obligation_id || item.occurrence_id);
}
export function obligationInboxTarget(obligation: ObvezaTvrtke): CompanyInboxTarget {
  return obligation.occurrence_id ? { occurrence_id: obligation.occurrence_id } : obligation.subject_type === "company" ? { company_obligation_id: obligation.id } : { site_obligation_id: obligation.id };
}
export async function getCompanyInboxPage(params: URLSearchParams, signal?: AbortSignal): Promise<InboxPage> {
  return (await api.get<InboxPage>(`/company-inbox?${params.toString()}`, { signal })).data;
}
export async function getAllCompanyInboxItems(params: URLSearchParams, signal?: AbortSignal): Promise<InboxItem[]> {
  const items = new Map<string, InboxItem>();
  for (let offset = 0; ; offset += 100) {
    const query = new URLSearchParams(params); query.set("limit", "100"); query.set("offset", String(offset));
    const page = await getCompanyInboxPage(query, signal);
    page.items.forEach((item) => items.set(item.id, item));
    if (!page.items.length || offset + page.items.length >= page.total) return [...items.values()];
  }
}
export async function getCompanyInboxDetail(id: string, signal?: AbortSignal): Promise<InboxDetail> {
  return (await api.get<InboxDetail>(`/company-inbox/${id}`, { signal })).data;
}
export async function getCompanyInboxContent(id: string, signal?: AbortSignal): Promise<Blob> {
  return (await api.get<Blob>(`/company-inbox/${id}/content`, { signal, responseType: "blob" })).data;
}
export async function getCompanyInboxSites(signal?: AbortSignal, includeInactive = false): Promise<InboxSite[]> {
  return (await api.get<InboxSite[]>("/company-inbox/gradilista", { signal, ...(includeInactive ? { params: { include_inactive: true } } : {}) })).data;
}
export async function getCompanyInboxObligations(gradilisteId?: string | null, signal?: AbortSignal): Promise<ObvezaTvrtke[]> {
  return (await api.get<ObvezaTvrtke[]>("/company-inbox/obligations", { signal, params: gradilisteId ? { gradiliste_id: gradilisteId } : {} })).data;
}
export async function uploadCompanyInboxFiles(files: File[], owner: InboxOwner, source: InboxUploadSource, batchId: string, target?: CompanyInboxTarget): Promise<{ batch_id: string; results: InboxUploadResult[] }> {
  if (owner.owner_kind === "radnik") throw new Error("Ovaj unos je samo za dokumente tvrtke i gradilišta.");
  oznaciAktivnost(); const data = new FormData();
  files.forEach((file) => data.append("files", file));
  Object.entries({ ...owner, ...target }).forEach(([key, value]) => { if (value != null) data.append(key, value); });
  data.append("source", source); data.append("batch_id", batchId);
  return (await api.post<{ batch_id: string; results: InboxUploadResult[] }>("/company-inbox/upload", data)).data;
}
export async function confirmCompanyInboxItem(id: string, body: CompanyInboxConfirmBody): Promise<InboxItem> {
  return (await api.post<InboxItem>(`/company-inbox/${id}/confirm`, body)).data;
}
export async function confirmCompanyInboxBatch(ids: string[]): Promise<InboxBatchResponse> {
  return (await api.post<InboxBatchResponse>("/company-inbox/confirm-batch", { ids })).data;
}
export async function rejectCompanyInboxBatch(ids: string[], reject_reason: string): Promise<InboxBatchResponse> {
  return (await api.post<InboxBatchResponse>("/company-inbox/reject-batch", { ids, reject_reason })).data;
}
export async function rejectCompanyInboxItem(id: string, reject_reason: string): Promise<InboxItem> {
  return (await api.post<InboxItem>(`/company-inbox/${id}/reject`, { reject_reason })).data;
}
export async function deleteCompanyInboxItem(id: string, reason?: string): Promise<number> {
  return (await api.delete<{ deleted_duplicates: number }>(`/company-inbox/${id}`, { data: { reason: reason?.trim() || null } })).data.deleted_duplicates;
}

export async function restoreCompanyInboxItem(id: string): Promise<InboxItem> { return (await api.post<InboxItem>(`/company-inbox/${id}/restore`)).data; }
